import { useCallback, useEffect, useState } from "react";
import { copyNumber, copyText, waplifyChatHref } from "./api";
import { formatWaiting, type CareWaitingItem, type Severity } from "./priority";
import { dangerBtn, ghostBtn, microLabel, pillTone, primaryBtn } from "./ui";

/**
 * "Parents waiting" row — a parent messaged a Babynama WhatsApp line and is waiting for
 * a human. Mirrors WorklistCard's row grammar (severity rail + wash, tabular scan
 * anchor, semantic pills, action pinned right) but carries care-specific content: the
 * parent's message, a source pill, and a COPYABLE suggested reply.
 *
 * SAFETY: side-effect free. "Open in waplify" is an <a> that only opens the chat window
 * where a HUMAN writes and sends. The suggested reply is copy-to-clipboard text — never
 * a send button, nothing auto-sends. See api.copyText / api.waplifyChatHref.
 *
 * Colour tokens are duplicated from WorklistCard deliberately (Horizon has no `slate`,
 * `red-50` is a 10%-alpha wash — see WorklistCard's note); a 3-line map is cheaper to
 * read here than a shared abstraction.
 */
const rail: Record<Severity, string> = {
  critical: "border-l-4 ![border-left-color:#dc2626]",
  warning: "border-l-4 ![border-left-color:#f59e0b]",
  muted:
    "border-l-4 ![border-left-color:#cbd5e1] dark:![border-left-color:#334155]",
};

const wash: Record<Severity, string> = {
  critical: "bg-crit-50 hover:bg-crit-100 dark:!bg-navy-800 dark:hover:!bg-navy-700",
  warning: "bg-white hover:bg-clinic-50 dark:!bg-navy-800 dark:hover:!bg-navy-700",
  muted: "bg-white hover:bg-clinic-50 dark:!bg-navy-800 dark:hover:!bg-navy-700",
};

const urgencyTone: Record<Severity, string> = {
  critical: "text-crit-600 dark:text-red-400",
  warning: "text-warn-600 dark:text-amber-400",
  muted: "text-clinic-500 dark:text-gray-400",
};

// The scan anchor is w-14 (56px) + gap-4 (16px) — align the suggested-reply block and
// the actions row under the content column, not under the anchor.
const CONTENT_INDENT = "ml-[72px]";

interface Props {
  item: CareWaitingItem;
  /**
   * Clear this parent's flag(s) from the queue. NOT a reply — it marks the backing
   * picard task(s) done so the item drops out; picard re-opens a flag only if the
   * parent messages again. Nothing here puts a message on the wire.
   */
  onDismiss: (item: CareWaitingItem) => void;
  pending: boolean;
  error: string | null;
}

export default function CareWaitingCard({
  item,
  onDismiss,
  pending,
  error,
}: Props) {
  const [copiedDraft, setCopiedDraft] = useState(false);
  const [copiedNum, setCopiedNum] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // "· also on Support2Auto" — or "· +3 more lines" once the list gets long. Neutral
  // context, never a severity colour: it says where else they wrote, not how urgent.
  const alsoOnNote =
    item.alsoOn.length === 0
      ? null
      : item.alsoOn.length > 2
      ? `· +${item.alsoOn.length} more lines`
      : `· also on ${item.alsoOn.join(", ")}`;

  const waHref = waplifyChatHref(item.deeplinkPhone);

  useEffect(() => {
    if (!copiedDraft) return undefined;
    const t = window.setTimeout(() => setCopiedDraft(false), 2000);
    return () => window.clearTimeout(t);
  }, [copiedDraft]);

  useEffect(() => {
    if (!copiedNum) return undefined;
    const t = window.setTimeout(() => setCopiedNum(false), 2000);
    return () => window.clearTimeout(t);
  }, [copiedNum]);

  // The ?phone= deep-link may not be live on every waplify build — copy the number too
  // so a CM can paste it into waplify's search. Fire-and-forget; the link opens either
  // way. (Same fallback the P2 "Message on WhatsApp" action uses.)
  const onOpenWaplify = useCallback(() => {
    void copyNumber(item.deeplinkPhone).then(setCopiedNum);
  }, [item.deeplinkPhone]);

  const onCopyDraft = useCallback(() => {
    if (!item.draftReply) return;
    void copyText(item.draftReply).then(setCopiedDraft);
  }, [item.draftReply]);

  return (
    <li
      data-testid="care-card"
      data-kind={item.kind}
      className={`relative flex flex-col gap-2 px-4 py-2.5 transition-colors first:rounded-t-[7px] last:rounded-b-[7px] motion-reduce:transition-none ${rail[item.severity]} ${wash[item.severity]}`}
    >
      <div className="flex items-start gap-4">
        {/* scan anchor — one tabular number, so a screenful ranks at a glance */}
        <div className="w-14 shrink-0 pt-0.5 text-center">
          <div
            className={`text-xl font-bold tabular-nums leading-none ${urgencyTone[item.severity]}`}
          >
            {item.urgencyValue}
          </div>
          <div className="mt-1 font-mono text-[9px] font-semibold uppercase leading-tight tracking-[0.12em] text-clinic-500 dark:text-gray-400">
            {item.urgencyLabel}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-[15px] font-semibold tabular-nums leading-tight text-clinic-900 dark:text-white">
              {item.mobile}
            </span>
            {/* kind carries the severity colour; the source number is neutral context */}
            <span
              data-testid="care-kind-pill"
              className={`rounded px-1.5 py-0.5 text-[11px] font-semibold leading-tight ${pillTone[item.severity]}`}
            >
              {item.kindLabel}
            </span>
            <span className="rounded bg-clinic-100 px-1.5 py-0.5 text-[11px] font-semibold leading-tight text-clinic-600 ring-1 ring-clinic-200 dark:bg-navy-700 dark:text-gray-300 dark:ring-navy-600">
              {item.sourceLabel}
            </span>
            {/* same parent, more than one Babynama line — neutral context, not severity */}
            {alsoOnNote && (
              <span
                data-testid="care-also-on"
                className="text-[11px] font-medium leading-tight text-clinic-500 dark:text-gray-400"
              >
                {alsoOnNote}
              </span>
            )}
          </div>

          {/* the parent's actual message — line-clamp-2 (tailwind 3.1 has no utility) */}
          <p className="mt-1 overflow-hidden text-[13px] leading-snug text-clinic-700 [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box] dark:text-gray-200">
            {item.messageExcerpt}
          </p>
          <p className="mt-0.5 text-xs tabular-nums leading-snug text-clinic-500 dark:text-gray-400">
            {item.reason} · waiting {formatWaiting(item.waitMinutes)}
          </p>

          {error && (
            <p
              role="alert"
              data-testid="care-card-error"
              className="mt-1.5 rounded bg-crit-100 px-2 py-1 text-xs font-medium text-crit-700 ring-1 ring-crit-200"
            >
              {error} — the card is unchanged. Retry, or reload the queue.
            </p>
          )}
        </div>

        {/* the human goes HERE to reply — a link, not a send */}
        <div className="flex shrink-0 items-center gap-1.5">
          {waHref && (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="care-open-waplify"
              onClick={onOpenWaplify}
              className={primaryBtn}
            >
              Open in waplify
            </a>
          )}

          {/*
            Dismiss CLEARS the flag(s) from the queue — it marks the backing task(s)
            done, nothing more. It does NOT reply to the parent (picard re-opens a flag
            the moment they message again). Two-step, like P2 "Mark done": a stray click
            must not silently empty the top lane.
          */}
          {confirming ? (
            <span
              role="group"
              aria-label="Confirm clear from queue"
              className="flex items-center gap-1.5 rounded-md bg-warn-50 px-2 py-1 ring-1 ring-warn-200 dark:!bg-navy-900 dark:ring-navy-700"
            >
              <span className="text-xs font-medium text-warn-700 dark:text-amber-200">
                Clear (no reply)?
              </span>
              <button
                type="button"
                data-testid="care-dismiss-confirm"
                disabled={pending}
                onClick={() => {
                  setConfirming(false);
                  onDismiss(item);
                }}
                className={dangerBtn}
              >
                {pending ? "Working…" : "Yes, clear"}
              </button>
              <button
                type="button"
                data-testid="care-dismiss-cancel"
                onClick={() => setConfirming(false)}
                className={ghostBtn}
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              data-testid="care-dismiss"
              disabled={pending}
              title="Clear from queue (does not reply)"
              aria-label="Clear from queue (does not reply)"
              onClick={() => setConfirming(true)}
              className={ghostBtn}
            >
              Dismiss
            </button>
          )}
        </div>
      </div>

      {/*
        Suggested reply — COPY, never send. Present only for non-medical flags; medical
        questions get no auto-draft (a doctor answers those in-app). The label reinforces
        that a human copies and sends it in waplify.
      */}
      {item.draftReply && (
        <div
          data-testid="care-draft"
          className={`${CONTENT_INDENT} rounded-md border border-clinic-200 bg-clinic-50 px-2.5 py-2 dark:border-navy-700 dark:!bg-navy-900`}
        >
          <div className="flex items-center justify-between gap-3">
            <span className={microLabel}>
              Suggested reply — copy &amp; send in waplify
            </span>
            <button
              type="button"
              data-testid="care-copy-draft"
              onClick={onCopyDraft}
              className={ghostBtn}
            >
              {copiedDraft ? "Copied ✓" : "Copy"}
            </button>
          </div>
          <p className="mt-1.5 text-[13px] leading-snug text-clinic-700 dark:text-gray-200">
            {item.draftReply}
          </p>
        </div>
      )}

      {/* Announced, not just shown — the copy is what makes the deep-link usable today. */}
      <span aria-live="polite" className="sr-only">
        {copiedNum ? "Number copied to clipboard" : ""}
      </span>
      {copiedNum && (
        <span
          data-testid="care-num-copied"
          className="pointer-events-none absolute right-3 top-1 rounded bg-clinic-900 px-2 py-0.5 text-[10px] font-medium text-white shadow"
        >
          Number copied
        </span>
      )}
    </li>
  );
}
